import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileText, 
  CheckCircle2, 
  AlertCircle, 
  RefreshCw, 
  Upload,
  Loader2,
  Server,
  Clock,
  Activity,
  Eye,
  EyeOff,
  Settings,
  Save,
  X
} from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';

const OCRManagement = () => {
  const { isDark } = useTheme();
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testErrorDetails, setTestErrorDetails] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [testType, setTestType] = useState('standard');
  const [showFullText, setShowFullText] = useState(false);
  const [error, setError] = useState('');
  const [showConfigEdit, setShowConfigEdit] = useState(false);
  const [isEditingConfig, setIsEditingConfig] = useState(false);
  const [editedConfig, setEditedConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [providers, setProviders] = useState([]);
  const [routing, setRouting] = useState(null);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [savingRouting, setSavingRouting] = useState(false);
  const [selectedPrimaryProvider, setSelectedPrimaryProvider] = useState('');
  const [selectedFallbackProvider, setSelectedFallbackProvider] = useState('');
  const [fallbackEnabled, setFallbackEnabled] = useState(true);

  useEffect(() => {
    loadStatus();
    loadConfig();
    loadProviders();
    loadRouting();
    // Auto-refresh every 2 minutes (reduced from 30 seconds to reduce server load)
    const interval = setInterval(() => {
      loadStatus();
      loadProviders();
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/admin/ocr/status');
      setStatus(response.data);
    } catch (err) {
      console.error('Error loading OCR status:', err);
      setError(err.response?.data?.error || 'Failed to load OCR status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await axios.get('/api/admin/ocr/config');
      setConfig(response.data.config);
    } catch (err) {
      console.error('Error loading OCR config:', err);
    }
  };

  const loadProviders = async () => {
    try {
      setLoadingProviders(true);
      const response = await axios.get('/api/admin/ocr/providers');
      setProviders(response.data.providers || []);
    } catch (err) {
      console.error('Error loading providers:', err);
      setError(err.response?.data?.error || 'Failed to load providers');
    } finally {
      setLoadingProviders(false);
    }
  };

  const loadRouting = async () => {
    try {
      const response = await axios.get('/api/admin/ocr/routing');
      setRouting(response.data.routing);
      if (response.data.primaryProvider) {
        setSelectedPrimaryProvider(response.data.primaryProvider.id);
      }
      if (response.data.fallbackProvider) {
        setSelectedFallbackProvider(response.data.fallbackProvider.id);
      }
      if (response.data.routing) {
        setFallbackEnabled(response.data.routing.fallbackEnabled !== false);
      }
    } catch (err) {
      console.error('Error loading routing:', err);
    }
  };

  const saveRouting = async () => {
    try {
      setSavingRouting(true);
      setError('');
      setSaveSuccess(false);

      const response = await axios.put('/api/admin/ocr/routing', {
        primaryProvider: selectedPrimaryProvider,
        fallbackEnabled: fallbackEnabled,
        fallbackProvider: selectedFallbackProvider
      });

      if (response.data.success) {
        setSaveSuccess(true);
        await loadRouting();
        await loadStatus();
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error saving routing:', err);
      setError(err.response?.data?.error || 'Failed to save routing configuration');
    } finally {
      setSavingRouting(false);
    }
  };

  const startEditingConfig = () => {
    // Initialize config for all providers
    const providersConfig = {};
    
    providers.forEach(provider => {
      const providerConfig = config?.providers?.[provider.id] || {};
      providersConfig[provider.id] = {
        enabled: providerConfig.enabled !== false,
        baseUrl: providerConfig.baseUrl || '',
        externalUrl: providerConfig.externalUrl || '',
        timeout: providerConfig.timeout || 60000,
        maxRetries: providerConfig.maxRetries || 3,
        apiKey: providerConfig.apiKey || ''
      };
    });
    
    setEditedConfig({
      providers: providersConfig
    });
    setIsEditingConfig(true);
    setSaveSuccess(false);
    setError('');
  };

  const cancelEditingConfig = () => {
    setIsEditingConfig(false);
    setEditedConfig(null);
    setError('');
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      setError('');
      setSaveSuccess(false);

      const response = await axios.put('/api/admin/ocr/config', editedConfig);

      if (response.data.success) {
        setSaveSuccess(true);
        setIsEditingConfig(false);
        setEditedConfig(null);
        
        // Reload configuration and status
        await loadConfig();
        await loadStatus();

        // Clear success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error saving OCR config:', err);
      setError(err.response?.data?.error || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateEditedConfig = (providerId, field, value) => {
    setEditedConfig({
      ...editedConfig,
      providers: {
        ...editedConfig.providers,
        [providerId]: {
          ...editedConfig.providers[providerId],
          [field]: value
        }
      }
    });
  };

  const testQuick = async () => {
    try {
      setTesting(true);
      setError('');
      setTestResult(null);
      const response = await axios.post('/api/admin/ocr/test-quick');
      setTestResult({
        type: 'quick',
        ...response.data
      });
    } catch (err) {
      console.error('Quick test failed:', err);
      setError(err.response?.data?.error || 'Quick test failed');
    } finally {
      setTesting(false);
    }
  };

  const testWithFile = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    try {
      setTesting(true);
      setError('');
      setTestResult(null);
      setTestErrorDetails(null);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('testType', testType);
      formData.append('languages', 'th,en'); // Add Thai and English language support

      const response = await axios.post('/api/admin/ocr/test', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setTestResult(response.data);
    } catch (err) {
      console.error('File test failed:', err);
      const data = err.response?.data;
      const details = data?.details;
      setError(data?.error || 'File test failed');
      setTestErrorDetails(details || null);
      setTestResult(null);
      if (details) console.error('Error details:', details);
    } finally {
      setTesting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf'];
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        setError(`File type ${ext} not allowed. Allowed types: ${allowedTypes.join(', ')}`);
        setSelectedFile(null);
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size exceeds 10MB limit');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'gray';
    if (status.serviceTest?.success) return 'green';
    if (status.serviceTest?.success === false) return 'red';
    return 'yellow';
  };

  const getStatusIcon = (status) => {
    if (!status) return <AlertCircle className="text-gray-400" />;
    if (status.serviceTest?.success) return <CheckCircle2 className="text-green-500" />;
    if (status.serviceTest?.success === false) return <AlertCircle className="text-red-500" />;
    return <AlertCircle className="text-yellow-500" />;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatTime = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <div className={`space-y-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'} min-h-screen p-6`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold flex items-center space-x-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <FileText className="h-7 w-7 text-blue-600" />
            <span>OCR Service Management</span>
          </h1>
          <p className={`mt-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Monitor and manage OCR providers (PaddleOCR & Typhoon OCR)
          </p>
        </div>
        <button
          onClick={loadStatus}
          disabled={loading}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            loading
              ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4`}>
          <div className="flex items-center space-x-2">
            <AlertCircle className="text-red-600 dark:text-red-400" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
          {testErrorDetails && (testErrorDetails.hint || testErrorDetails.bodyError || testErrorDetails.status) && (
            <div className={`mt-3 pt-3 border-t border-red-200 dark:border-red-800 text-sm ${testErrorDetails.hint ? 'space-y-1' : ''}`}>
              {testErrorDetails.status != null && (
                <div className="text-red-700 dark:text-red-300">HTTP status: {testErrorDetails.status}</div>
              )}
              {testErrorDetails.bodyError && (
                <div className="text-red-700 dark:text-red-300">Response: {testErrorDetails.bodyError}</div>
              )}
              {testErrorDetails.hint && (
                <div className="text-amber-700 dark:text-amber-300 font-medium">Hint: {testErrorDetails.hint}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Provider Selection Card */}
      <div className={`rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-semibold flex items-center space-x-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Settings className="h-5 w-5 text-blue-600" />
            <span>Provider Selection</span>
          </h2>
        </div>

        {loadingProviders ? (
          <div className="text-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600 mx-auto mb-2" />
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading providers...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Primary Provider Selection */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Primary OCR Provider
              </label>
              <select
                value={selectedPrimaryProvider}
                onChange={(e) => setSelectedPrimaryProvider(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">Select provider...</option>
                {providers.filter(p => p.enabled).map(provider => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name} {provider.status === 'available' ? '✓' : '✗'}
                  </option>
                ))}
              </select>
            </div>

            {/* Fallback Toggle */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="fallbackEnabled"
                checked={fallbackEnabled}
                onChange={(e) => setFallbackEnabled(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              <label htmlFor="fallbackEnabled" className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Enable automatic fallback to secondary provider
              </label>
            </div>

            {/* Fallback Provider Selection */}
            {fallbackEnabled && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Fallback OCR Provider
                </label>
                <select
                  value={selectedFallbackProvider}
                  onChange={(e) => setSelectedFallbackProvider(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Select provider...</option>
                  {providers
                    .filter(p => p.enabled && p.id !== selectedPrimaryProvider)
                    .map(provider => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name} {provider.status === 'available' ? '✓' : '✗'}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={saveRouting}
              disabled={savingRouting || !selectedPrimaryProvider}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                savingRouting || !selectedPrimaryProvider
                  ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {savingRouting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save Routing Configuration</span>
                </>
              )}
            </button>

            {/* Provider Status Table */}
            <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Provider Status
              </h3>
              <div className="space-y-2">
                {providers.map(provider => (
                  <div
                    key={provider.id}
                    className={`p-3 rounded-lg border ${
                      isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {provider.name}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {provider.type} • {provider.requiresApiKey ? 'Requires API Key' : 'No API Key Required'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            provider.status === 'available'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}
                        >
                          {provider.status === 'available' ? 'Available' : 'Unavailable'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status Card */}
      <div className={`rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-lg font-semibold flex items-center space-x-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Server className="h-5 w-5 text-blue-600" />
            <span>Service Status</span>
          </h2>
          {loading && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
        </div>

        {loading && !status ? (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading status...</p>
          </div>
        ) : status ? (
          <div className="space-y-4">
            {/* Service Status */}
            <div className="flex items-center space-x-3">
              {getStatusIcon(status.serviceTest)}
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {status.serviceTest?.success ? 'Service Available' : 'Service Unavailable'}
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {status.serviceTest?.message || 'Unknown status'}
                </p>
              </div>
            </div>

            {/* Configuration */}
            {config && !isEditingConfig && (
              <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Provider Configurations
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={startEditingConfig}
                      disabled={providers.length === 0}
                      className={`flex items-center space-x-1 text-xs px-3 py-1.5 rounded transition-colors ${
                        providers.length === 0
                          ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                          : isDark
                          ? 'bg-blue-900 hover:bg-blue-800 text-blue-200'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      <Settings className="h-3 w-3" />
                      <span>Edit Configurations</span>
                    </button>
                  </div>
                </div>

                {saveSuccess && (
                  <div className="mb-3 p-2 rounded bg-green-100 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <span className="text-sm text-green-800 dark:text-green-200">Configuration saved successfully!</span>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {providers.map(provider => {
                    const providerConfig = config?.providers?.[provider.id] || {};
                    return (
                      <div key={provider.id} className={`p-3 rounded-lg border ${
                        isDark ? 'bg-gray-700/30 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {provider.name}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {provider.id}
                            </p>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            providerConfig.enabled !== false
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                          }`}>
                            {providerConfig.enabled !== false ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 gap-2 text-xs">
                          {providerConfig.baseUrl && (
                            <div>
                              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Internal URL:</span>
                              <span className={`ml-2 font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                {providerConfig.baseUrl}
                              </span>
                            </div>
                          )}
                          {providerConfig.externalUrl && (
                            <div>
                              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>External URL:</span>
                              <span className={`ml-2 font-mono ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                                {providerConfig.externalUrl}
                              </span>
                            </div>
                          )}
                          <div className="flex space-x-4">
                            {providerConfig.timeout && (
                              <div>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Timeout:</span>
                                <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {providerConfig.timeout}ms
                                </span>
                              </div>
                            )}
                            {providerConfig.maxRetries !== undefined && (
                              <div>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Max Retries:</span>
                                <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {providerConfig.maxRetries}
                                </span>
                              </div>
                            )}
                            <div>
                              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>API Key:</span>
                              <span className={`ml-1 ${providerConfig.apiKey ? (isDark ? 'text-green-400' : 'text-green-600') : (isDark ? 'text-yellow-400' : 'text-yellow-600')}`}>
                                {providerConfig.apiKey ? `${providerConfig.apiKey.substring(0, 8)}...` : 'Not set'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={() => setShowConfigEdit(!showConfigEdit)}
                  className={`mt-3 text-xs ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  {showConfigEdit ? '▲ Hide Details' : '▼ Show Details'}
                </button>

                {showConfigEdit && (
                  <div className={`mt-3 p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      <strong>Note:</strong> The Internal URL is used by the backend server to communicate with the OCR service. 
                      The External URL is for direct access from your browser or external systems.
                    </p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Configuration file: <code className={`${isDark ? 'text-blue-400' : 'text-blue-600'}`}>backend/config/ocr-config.json</code>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Configuration Editor */}
            {isEditingConfig && editedConfig && (
              <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Edit Provider Configurations
                  </h3>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={cancelEditingConfig}
                      disabled={saving}
                      className={`flex items-center space-x-1 text-xs px-3 py-1.5 rounded transition-colors ${
                        saving
                          ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                          : isDark
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      <X className="h-3 w-3" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={saveConfig}
                      disabled={saving}
                      className={`flex items-center space-x-1 text-xs px-3 py-1.5 rounded transition-colors ${
                        saving
                          ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {saving ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="h-3 w-3" />
                          <span>Save All Configurations</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Loop through all providers */}
                  {providers.map((provider, index) => (
                    <div key={provider.id} className={`p-4 rounded-lg border ${
                      isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-300'
                    }`}>
                      {/* Provider Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {provider.name}
                          </h4>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {provider.type} • {provider.id}
                          </p>
                        </div>
                        {/* Enabled Toggle */}
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`${provider.id}-enabled`}
                            checked={editedConfig.providers[provider.id]?.enabled}
                            onChange={(e) => updateEditedConfig(provider.id, 'enabled', e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <label htmlFor={`${provider.id}-enabled`} className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Enabled
                          </label>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {/* Internal URL */}
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Internal URL <span className="text-blue-500">(Backend use)</span>
                          </label>
                          <input
                            type="text"
                            value={editedConfig.providers[provider.id]?.baseUrl || ''}
                            onChange={(e) => updateEditedConfig(provider.id, 'baseUrl', e.target.value)}
                            className={`w-full px-3 py-2 text-sm font-mono rounded-lg border ${
                              isDark
                                ? 'bg-gray-600 border-gray-500 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder={`http://${provider.id}:8000`}
                          />
                          <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                            Used by backend to communicate with OCR service (e.g., Docker service name)
                          </p>
                        </div>

                        {/* External URL */}
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            External URL <span className="text-green-500">(Direct access)</span>
                          </label>
                          <input
                            type="text"
                            value={editedConfig.providers[provider.id]?.externalUrl || ''}
                            onChange={(e) => updateEditedConfig(provider.id, 'externalUrl', e.target.value)}
                            className={`w-full px-3 py-2 text-sm font-mono rounded-lg border ${
                              isDark
                                ? 'bg-gray-600 border-gray-500 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                            placeholder="http://localhost:8000"
                          />
                          <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                            Used for direct access from browser or external systems
                          </p>
                        </div>

                        {/* API Key */}
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            API Key <span className="text-xs text-gray-500">(for cross-server security)</span>
                          </label>
                          <input
                            type="password"
                            value={editedConfig.providers[provider.id]?.apiKey || ''}
                            onChange={(e) => updateEditedConfig(provider.id, 'apiKey', e.target.value)}
                            placeholder="Generated automatically or set manually"
                            className={`w-full px-3 py-2 text-sm font-mono rounded-lg border ${
                              isDark
                                ? 'bg-gray-600 border-gray-500 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                          <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                            Leave empty for no authentication (same server only)
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          {/* Timeout */}
                          <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              Timeout (ms)
                            </label>
                            <input
                              type="number"
                              value={editedConfig.providers[provider.id]?.timeout || 60000}
                              onChange={(e) => updateEditedConfig(provider.id, 'timeout', parseInt(e.target.value) || 60000)}
                              className={`w-full px-3 py-2 text-sm rounded-lg border ${
                                isDark
                                  ? 'bg-gray-600 border-gray-500 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              min="1000"
                              max="300000"
                              step="1000"
                            />
                          </div>

                          {/* Max Retries */}
                          <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              Max Retries
                            </label>
                            <input
                              type="number"
                              value={editedConfig.providers[provider.id]?.maxRetries || 3}
                              onChange={(e) => updateEditedConfig(provider.id, 'maxRetries', parseInt(e.target.value) || 3)}
                              className={`w-full px-3 py-2 text-sm rounded-lg border ${
                                isDark
                                  ? 'bg-gray-600 border-gray-500 text-white'
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                              min="0"
                              max="10"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Warning Note */}
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <div className="flex items-start space-x-2">
                      <AlertCircle className={`h-4 w-4 mt-0.5 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                      <div>
                        <p className={`text-xs font-medium mb-1 ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>
                          Important:
                        </p>
                        <p className={`text-xs ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                          Changes will update the configuration file. Backend service may need to be restarted to apply some changes.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Service Test Details */}
            {status.serviceTest?.details && (
              <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Service Details
                </h3>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Model:</span>
                    <span className={isDark ? 'text-white' : 'text-gray-900'}>
                      {status.serviceTest.details.model || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Status:</span>
                    <span className={isDark ? 'text-white' : 'text-gray-900'}>
                      {status.serviceTest.details.status || 'N/A'}
                    </span>
                  </div>
                  {status.serviceTest.details.ready !== undefined && (
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Ready:</span>
                      <span className={isDark ? 'text-white' : 'text-gray-900'}>
                        {status.serviceTest.details.ready ? 'Yes' : 'No'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Timestamp */}
            <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                Last updated: {new Date(status.timestamp).toLocaleString()}
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Failed to load status</p>
          </div>
        )}
      </div>

      {/* Test Section */}
      <div className={`rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-6`}>
        <h2 className={`text-lg font-semibold mb-4 flex items-center space-x-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Activity className="h-5 w-5 text-blue-600" />
          <span>Test OCR Service</span>
        </h2>

        {/* Quick Test */}
        <div className="mb-6">
          <h3 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Quick Connectivity Test
          </h3>
          <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Test if the OCR service is reachable and responding
          </p>
          <button
            onClick={testQuick}
            disabled={testing}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              testing
                ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {testing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            <span>Run Quick Test</span>
          </button>
        </div>

        {/* File Test */}
        <div className={`pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Test with File Upload
          </h3>
          <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Upload an image or PDF to test OCR extraction
          </p>

          <div className="space-y-4">
            {/* Test Type Selection */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Test Type
              </label>
              <select
                value={testType}
                onChange={(e) => setTestType(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="standard">Standard Text Extraction</option>
                <option value="business-card">Business Card Extraction</option>
              </select>
            </div>

            {/* File Upload */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Select File (JPG, PNG, PDF - Max 10MB)
              </label>
              <div className="flex items-center space-x-4">
                <label className={`flex items-center space-x-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}>
                  <Upload className="h-4 w-4" />
                  <span>Choose File</span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                {selectedFile && (
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {selectedFile.name} ({formatBytes(selectedFile.size)})
                  </span>
                )}
              </div>
            </div>

            {/* Test Button */}
            <button
              onClick={testWithFile}
              disabled={testing || !selectedFile}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                testing || !selectedFile
                  ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {testing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              <span>Test OCR Extraction</span>
            </button>
          </div>
        </div>
      </div>

      {/* Test Results */}
      {testResult && (
        <div className={`rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold flex items-center space-x-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span>Test Results</span>
            </h2>
            <button
              onClick={() => setTestResult(null)}
              className={`text-sm ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
            >
              Clear
            </button>
          </div>

          {testResult.success ? (
            <div className="space-y-4">
              {/* Metadata */}
              {testResult.result?.metadata && (
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Processing Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>File:</span>
                      <span className={`ml-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {testResult.result.metadata.fileName}
                      </span>
                    </div>
                    <div>
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Size:</span>
                      <span className={`ml-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatBytes(testResult.result.metadata.fileSize)}
                      </span>
                    </div>
                    <div>
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Type:</span>
                      <span className={`ml-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {testResult.result.metadata.testType}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4 text-blue-500" />
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Time:</span>
                      <span className={`ml-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatTime(testResult.result.metadata.processingTime)}
                      </span>
                    </div>
                    <div>
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Text Length:</span>
                      <span className={`ml-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {testResult.result.metadata.textLength} characters
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Extracted Text */}
              {testResult.result?.extractedText && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Extracted Text
                    </h3>
                    <button
                      onClick={() => setShowFullText(!showFullText)}
                      className={`text-xs flex items-center space-x-1 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
                    >
                      {showFullText ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      <span>{showFullText ? 'Show Less' : 'Show Full'}</span>
                    </button>
                  </div>
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <pre className={`text-sm whitespace-pre-wrap font-mono ${
                      isDark ? 'text-gray-300' : 'text-gray-900'
                    }`}>
                      {showFullText
                        ? testResult.result.extractedText
                        : testResult.result.extractedText.substring(0, 500) + 
                          (testResult.result.extractedText.length > 500 ? '...' : '')}
                    </pre>
                  </div>
                </div>
              )}

              {/* Parsed Business Card Data */}
              {testResult.result?.parsedData && (
                <div>
                  <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Parsed Business Card Data
                  </h3>
                  <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <pre className={`text-sm whitespace-pre-wrap font-mono ${
                      isDark ? 'text-gray-300' : 'text-gray-900'
                    }`}>
                      {JSON.stringify(testResult.result.parsedData, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={`p-4 rounded-lg ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
              <p className={isDark ? 'text-red-300' : 'text-red-800'}>
                {testResult.error || 'Test failed'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OCRManagement;
